class CartRemoveButton extends HTMLElement {
	constructor() {
		super();

		this.addEventListener("click", (event) => {
			event.preventDefault();
			
			// Prevent multiple clicks
			if (this.classList.contains('loading')) return;
			
			const cartItems =
				this.closest("cart-items") || this.closest("cart-drawer-items");
			
			// Check if this item has a bundled addon
			const cartItem = this.closest('.cart-item');
			const hasBundledAddon = cartItem && cartItem.classList.contains('cart-item--has-bundle');
			
			console.log('🗑️ REMOVE BUTTON CLICKED:', {
				lineIndex: this.dataset.index,
				hasBundledAddon: hasBundledAddon,
				cartItemClasses: cartItem ? cartItem.className : 'no cart-item found',
				willUseBundledRemoval: hasBundledAddon
			});
			
			// Add loading state to button
			const button = this.querySelector('button');
			if (button) {
				button.disabled = true;
				button.classList.add('loading');
				const originalText = button.querySelector('.button-label')?.textContent;
				if (button.querySelector('.button-label')) {
					button.querySelector('.button-label').textContent = 'Removing...';
				}
				// Store original text for restoration on error
				button.dataset.originalText = originalText;
			}
			
			if (hasBundledAddon) {
				// Remove both the main product and its bundled addon
				console.log('🔀 Calling removeBundledItems()');
				this.removeBundledItems(cartItems).catch((error) => {
					// Restore button state on error
					console.error('Error in removeBundledItems:', error);
					if (button) {
						button.disabled = false;
						button.classList.remove('loading');
						if (button.querySelector('.button-label') && button.dataset.originalText) {
							button.querySelector('.button-label').textContent = button.dataset.originalText;
						}
					}
				});
			} else {
				// Standard single item removal
				console.log('⚡ Using standard updateQuantity()');
				cartItems.updateQuantity(this.dataset.index, 0);
			}
		});
	}
	
	async removeBundledItems(cartItems) {
		// Enable loading state
		const line = this.dataset.index;
		if (cartItems.enableLoading) cartItems.enableLoading(line);
		
		try {
			// Get FRESH cart state to find the bundled addon
			// This is critical because line numbers may have shifted if other items were removed
			const cartResponse = await fetch('/cart.js');
			const cart = await cartResponse.json();
			
			console.log('🛒 CART DEBUG - Before removal:', {
				totalItems: cart.items.length,
				items: cart.items.map((item, idx) => ({
					line: idx + 1,
					title: item.product_title,
					quantity: item.quantity,
					bundleId: item.properties?._bundle_id,
					isAddon: !!item.properties?._bundled_with_main
				}))
			});
			
			// IMPORTANT: The line number from the button may be stale
			// Find the actual current line number by matching the item's unique properties
			const buttonLineIndex = parseInt(line) - 1;
			
			// Try to find the item by its position first (might have shifted)
			let mainItem = cart.items[buttonLineIndex];
			let actualMainLineIndex = buttonLineIndex;
			
			// Verify this is actually a main product (not an addon)
			if (mainItem && mainItem.properties && mainItem.properties._bundled_with_main) {
				// This is an addon, not a main product - something is wrong
				console.error('⚠️ Line number points to an addon, not a main product. Searching for main product...');
				// Search for the first main product (not an addon)
				mainItem = null;
				for (let i = 0; i < cart.items.length; i++) {
					if (!cart.items[i].properties || !cart.items[i].properties._bundled_with_main) {
						mainItem = cart.items[i];
						actualMainLineIndex = i;
						break;
					}
				}
			}
			
			if (!mainItem) {
				throw new Error('Could not find main product to remove');
			}
			
			console.log('📦 Main product to remove:', {
				buttonLine: line,
				actualLine: actualMainLineIndex + 1,
				title: mainItem.product_title,
				bundleId: mainItem.properties?._bundle_id
			});
			
			// Find the bundled addon
			const bundleId = mainItem.properties && mainItem.properties._bundle_id;
			let addonLine = null;
			let addonIndex = null;
			
			if (bundleId) {
				// Find addon by matching bundle_id
				cart.items.forEach((item, index) => {
					if (item.properties && 
					    item.properties._bundled_with_main && 
					    item.properties._bundle_id === bundleId) {
						addonLine = index + 1; // 1-based line number
						addonIndex = index;
						console.log('🏷️  Found bundled addon:', {
							line: addonLine,
							title: item.product_title,
							bundleId: item.properties._bundle_id
						});
					}
				});
			}
			
			// Fallback: look for addon near main product
			if (!addonLine && actualMainLineIndex + 1 < cart.items.length) {
				const nextItem = cart.items[actualMainLineIndex + 1];
				if (nextItem.properties && nextItem.properties._bundled_with_main) {
					addonIndex = actualMainLineIndex + 1;
					addonLine = addonIndex + 1; // 1-based
					console.log('🏷️  Found addon adjacent to main product:', {
						line: addonLine,
						title: nextItem.product_title
					});
				}
			}
			
			// Strategy: Remove addon first (if it exists), then main product
			// This avoids index shifting issues
			let parsedState;
			
			if (addonLine) {
				console.log('🔄 Step 1: Removing addon first');
				console.log(`🗑️ Removing addon at line ${addonLine}`);
				
				const addonBody = JSON.stringify({
					line: parseInt(addonLine),
					quantity: 0,
					sections: cartItems.getSectionsToRender().map((section) => section.section),
					sections_url: window.location.pathname,
				});
				
				const addonResponse = await fetch(`${routes.cart_change_url}`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Accept': 'application/json',
						'X-Requested-With': 'XMLHttpRequest'
					},
					body: addonBody
				});
				
				const responseText = await addonResponse.text();
				console.log('📥 Addon removal response:', {
					status: addonResponse.status,
					ok: addonResponse.ok,
					responsePreview: responseText.substring(0, 200)
				});
				
				if (!addonResponse.ok) {
					console.error(`❌ Failed to remove addon at line ${addonLine}. Response:`, responseText);
					// If addon removal fails, try to continue with main product removal
					console.warn('⚠️ Continuing to remove main product despite addon removal failure');
				} else {
					try {
						parsedState = JSON.parse(responseText);
						console.log(`✅ Addon removed. Cart now has ${parsedState.item_count} items`);
					} catch (e) {
						console.error('Failed to parse addon removal response as JSON:', e);
					}
				}
			}
			
			// Now remove the main product - its line number may have shifted
			const updatedCart = await fetch('/cart.js').then(r => r.json());
			const newMainLine = updatedCart.items.findIndex(item => 
				item.product_id === mainItem.product_id && 
				item.variant_id === mainItem.variant_id &&
				item.properties?._bundle_id === bundleId
			) + 1; // Convert to 1-based
			
			console.log('🔄 Step 2: Removing main product');
			console.log(`🗑️ Removing main product at line ${newMainLine} (was line ${line})`);
			
			const mainBody = JSON.stringify({
				line: newMainLine,
				quantity: 0,
				sections: cartItems.getSectionsToRender().map((section) => section.section),
				sections_url: window.location.pathname,
			});
			
			const mainResponse = await fetch(`${routes.cart_change_url}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json',
					'X-Requested-With': 'XMLHttpRequest'
				},
				body: mainBody
			});
			
			if (!mainResponse.ok) {
				const errorText = await mainResponse.text();
				console.error(`❌ Failed to remove main product at line ${newMainLine}:`, errorText);
				throw new Error(`Failed to remove main product at line ${newMainLine}`);
			}
			
			parsedState = await mainResponse.json();
			console.log(`✅ Main product removed. Cart now has ${parsedState.item_count} items`);
			
			console.log('✅ Both items removed successfully. Final state:', {
				itemCount: parsedState.item_count,
				sectionsReceived: !!parsedState.sections
			});
			
			// Update the UI with the final response
			cartItems.classList.toggle("is-empty", parsedState.item_count === 0);
			const cartDrawerWrapper = document.querySelector("cart-drawer");
			const cartFooter = document.getElementById("main-cart-footer");

			if (cartFooter)
				cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
			if (cartDrawerWrapper)
				cartDrawerWrapper.classList.toggle("is-empty", parsedState.item_count === 0);

			if (parsedState.sections) {
				cartItems.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document.getElementById(section.id).querySelector(section.selector) ||
						document.getElementById(section.id);
					elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector
					);
				});
			}
			
			// Update cart shipping if it exists
			if (cartItems.cartShipping) cartItems.cartShipping();
			
			// Publish cart update event
			if (typeof publish === 'function') {
				publish('cart:update', { source: 'cart-items' });
			}
			
		} catch (error) {
			console.error('Error removing bundled items:', error);
			// Fallback to standard removal
			cartItems.updateQuantity(this.dataset.index, 0);
		} finally {
			// Ensure loading state is removed even if there's an error
			const button = this.querySelector('button');
			if (button) {
				button.disabled = false;
				button.classList.remove('loading');
				if (button.querySelector('.button-label') && button.dataset.originalText) {
					button.querySelector('.button-label').textContent = button.dataset.originalText;
				}
			}
		}
	}
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
	constructor() {
		super();
		this.lineItemStatusElement =
			document.getElementById("shopping-cart-line-item-status") ||
			document.getElementById("CartDrawer-LineItemStatus");

		if (document.querySelector(".cart-shipping")) {
			this.minSpend = document.querySelector(".cart-shipping").dataset.minSpend;
			this.minTotal = Math.round(this.minSpend * (Shopify.currency.rate || 1));
			this.cartShipping();
		}

		const debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, ON_CHANGE_DEBOUNCE_TIMER);

		this.addEventListener("change", debouncedOnChange.bind(this));
	}

	cartUpdateUnsubscriber = undefined;

	cartShipping() {
		let progressPrev = getComputedStyle(
			document.querySelector(".cart-shipping__progress-current")
		).getPropertyValue("width");
		document.documentElement.style.setProperty("--progress-prev", progressPrev);

		this.total = document.querySelector(".cart-shipping").dataset.total;
		this.progress = (this.total / this.minTotal) * 100;
		if (this.progress > 100) this.progress = 100;

		if (this.minTotal > this.total) {
			let amount = this.minTotal - this.total;
			let message = document
				.querySelector(".cart-shipping")
				.dataset.message.replace("||amount||", formatMoney(amount));
			document.querySelector(".cart-shipping__message_default").innerText =
				message;
			document
				.querySelector(".cart-shipping__message_success")
				.classList.remove("active");
			document
				.querySelector(".cart-shipping__message_default")
				.classList.add("active");
		} else {
			document
				.querySelector(".cart-shipping__message_default")
				.classList.remove("active");
			document
				.querySelector(".cart-shipping__message_success")
				.classList.add("active");
		}

		document.querySelector(".cart-shipping__progress-current").style.width =
			this.progress + "%";
	}

	connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(event) => {
				if (event.source === "cart-items") {
					return;
				}
				this.onCartUpdate();
			}
		);
		buttonHoverEffects();

		const clearCartButton = document.getElementById("clear-cart");
		if (clearCartButton) {
			clearCartButton.addEventListener("click", () => {
				this.clearAllItems();
			});
		}
	}

	clearAllItems() {
		fetch("/cart/clear.js", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Requested-With": "XMLHttpRequest",
			},
		})
			.then((response) => response.json())
			.then((data) => {
				console.log("Cart cleared", data);
				window.location.reload();
			})
			.catch((error) => {
				console.error("Error clearing cart:", error);
			});
	}

	disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber();
		}
	}

	onChange(event) {
		this.updateQuantity(
			event.target.dataset.index,
			event.target.value,
			document.activeElement.getAttribute("name")
		);
	}

	onCartUpdate() {
		//fetch(`${routes.cart_url}?section_id=main-cart-items`)
		fetch(`${routes.cart_url}?section_id=main-cart`)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, "text/html");
				const sourceQty = html.querySelector("cart-items");
				this.innerHTML = sourceQty.innerHTML;
			})
			.catch((e) => {
				console.error(e);
			});
	}

	getSectionsToRender() {
		return [
			{
				id: "main-cart-items",
				section: document.getElementById("main-cart-items").dataset.id,
				selector: ".js-contents",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
			{
				id: "cart-live-region-text",
				section: "cart-live-region-text",
				selector: ".shopify-section",
			},
			{
				id: "main-cart-footer",
				section: document.getElementById("main-cart-footer").dataset.id,
				selector: ".js-contents-2",
			},
			{
        id: 'main-cart-shipping',
        section: document.getElementById('main-cart-shipping').dataset.id || null,
        selector: '.js-contents-shipping'
      }
		];
	}

	updateQuantity(line, quantity, name) {
		this.enableLoading(line);
		this.querySelectorAll(".quantity__button").forEach((button) =>
			button.classList.add("disabled")
		);

		if (document.querySelector('.cart-shipping')) {
      let progressPrev = getComputedStyle(document.querySelector('.cart-shipping__progress-current')).getPropertyValue('width');    
      document.documentElement.style.setProperty('--progress-prev', progressPrev);
    }

		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => {
				return response.text();
			})
			.then((state) => {
				const parsedState = JSON.parse(state);
				const quantityElement =
					document.getElementById(`Quantity-${line}`) ||
					document.getElementById(`Drawer-quantity-${line}`);
				const items = document.querySelectorAll(".cart-item");
				if (parsedState.errors) {
					quantityElement.value = quantityElement.getAttribute("value");
					this.updateLiveRegions(line, parsedState.errors);
					return;
				}

				this.classList.toggle("is-empty", parsedState.item_count === 0);
				const cartDrawerWrapper = document.querySelector("cart-drawer");
				const cartFooter = document.getElementById("main-cart-footer");

				if (cartFooter)
					cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
				if (cartDrawerWrapper)
					cartDrawerWrapper.classList.toggle(
						"is-empty",
						parsedState.item_count === 0
					);

				this.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document
							.getElementById(section.id)
							.querySelector(section.selector) ||
						document.getElementById(section.id);
					elementToReplace.innerHTML = this.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector
					);
				});
				const updatedValue = parsedState.items[line - 1]
					? parsedState.items[line - 1].quantity
					: undefined;
				let message = "";
				if (
					items.length === parsedState.items.length &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === "undefined") {
						message = window.cartStrings.error;
					} else {
						message = window.cartStrings.quantityError.replace(
							"[quantity]",
							updatedValue
						);
					}
				}
				this.updateLiveRegions(line, message);

				const lineItem =
					document.getElementById(`CartItem-${line}`) ||
					document.getElementById(`CartDrawer-Item-${line}`);
				if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? trapFocus(
								cartDrawerWrapper,
								lineItem.querySelector(`[name="${name}"]`)
						  )
						: lineItem.querySelector(`[name="${name}"]`).focus();
				} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
					trapFocus(
						cartDrawerWrapper.querySelector(".drawer__inner-empty"),
						cartDrawerWrapper.querySelector("a")
					);
				} else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
					trapFocus(
						cartDrawerWrapper,
						document.querySelector(".cart-item__name")
					);
				}
				publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
			})
			.catch(() => {
				this.querySelectorAll(".loading-overlay").forEach((overlay) =>
					overlay.classList.add("hidden")
				);
				this.querySelectorAll(".quantity__button").forEach((button) =>
					button.classList.remove("disabled")
				);
				const errors =
					document.getElementById("cart-errors") ||
					document.getElementById("CartDrawer-CartErrors");
				errors.textContent = window.cartStrings.error;
			})
			.finally(() => {
				this.querySelectorAll(".quantity__button").forEach((button) =>
					button.classList.remove("disabled")
				);
				if (document.querySelector('.cart-shipping')) {
          this.cartShipping();
        }
				this.disableLoading(line);
			});
	}

	updateLiveRegions(line, message) {
		const lineItemError =
			document.getElementById(`Line-item-error-${line}`) ||
			document.getElementById(`CartDrawer-LineItemError-${line}`);
		if (lineItemError)
			lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

		this.lineItemStatusElement.setAttribute("aria-hidden", true);

		const cartStatus =
			document.getElementById("cart-live-region-text") ||
			document.getElementById("CartDrawer-LiveRegionText");
		cartStatus.setAttribute("aria-hidden", false);

		setTimeout(() => {
			cartStatus.setAttribute("aria-hidden", true);
		}, 1000);
	}

	getSectionInnerHTML(html, selector) {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	enableLoading(line) {
		const mainCartItems =
			document.getElementById("main-cart-items") ||
			document.getElementById("CartDrawer-CartItems");
		mainCartItems.classList.add("cart__items--disabled");

		const cartItemElements = this.querySelectorAll(
			`#CartItem-${line} .loading-overlay`
		);
		const cartDrawerItemElements = this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading-overlay`
		);

		[...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
			overlay.classList.remove("hidden")
		);

		document.activeElement.blur();
		this.lineItemStatusElement.setAttribute("aria-hidden", false);
	}

	disableLoading(line) {
		const mainCartItems =
			document.getElementById("main-cart-items") ||
			document.getElementById("CartDrawer-CartItems");
		mainCartItems.classList.remove("cart__items--disabled");

		const cartItemElements = this.querySelectorAll(
			`#CartItem-${line} .loading-overlay`
		);
		const cartDrawerItemElements = this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading-overlay`
		);

		cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
		cartDrawerItemElements.forEach((overlay) =>
			overlay.classList.add("hidden")
		);
	}
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
